import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const FALLBACK_OPTIONS = [
  { value: "all", label: "All emails",
    description: "Everything — order updates, membership changes, and news from your club." },
  { value: "important_only", label: "Important only",
    description: "Order status updates and account changes. No club announcements." },
  { value: "no_marketing", label: "No marketing",
    description: "Order updates and club announcements. No marketing promotions." },
  { value: "opt_out", label: "Opt out (security only)",
    description: "Only password resets and account-security notices." },
];

/**
 * Member-facing card for choosing outbound-email preference (iter62).
 * Renders four preference tiers; PATCHes `/auth/me/email-preferences` on save.
 */
export default function EmailPreferencesCard({ user, onSaved }) {
  const [options, setOptions] = useState(FALLBACK_OPTIONS);
  const [choice, setChoice] = useState(user?.email_preferences || "all");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me/email-preferences");
        if (cancelled) return;
        if (Array.isArray(data?.options) && data.options.length > 0) {
          setOptions(data.options);
        }
        if (data?.preference) setChoice(data.preference);
      } catch (_err) {
        // Non-fatal — fall back to the seeded catalog.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const { data } = await api.patch("/auth/me/email-preferences", { preference: choice });
      onSaved?.(data);
      setFeedback({ type: "success", text: "Email preference saved." });
      setTimeout(() => setFeedback(null), 2500);
    } catch (err) {
      setFeedback({ type: "error", text: formatApiErrorDetail(err) || "Could not update preference." });
    } finally {
      setBusy(false);
    }
  };

  const dirty = choice !== (user?.email_preferences || "all");

  return (
    <div className="surface-card p-8" data-testid="email-preferences-card">
      <div className="overline">Notifications</div>
      <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Email Preferences</h2>
      <p className="text-sm text-[#4A4A4A] mt-2">
        Choose how many emails you&apos;d like to receive from the pro shop. Security notices
        (password resets, account changes) are always sent regardless of this preference.
      </p>

      <fieldset className="mt-6 space-y-3" data-testid="email-preferences-options">
        <legend className="sr-only">Email preference</legend>
        {options.map((opt) => {
          const active = choice === opt.value;
          return (
            <label
              key={opt.value}
              htmlFor={`email-pref-${opt.value}`}
              className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${
                active
                  ? "border-[#1A422D] bg-[#F9F8F4]"
                  : "border-[#E5E3D8] bg-white hover:border-[#B59A5A]"
              }`}
              data-testid={`email-pref-option-${opt.value}`}
            >
              <input
                id={`email-pref-${opt.value}`}
                type="radio"
                name="email_preference"
                value={opt.value}
                checked={active}
                onChange={() => setChoice(opt.value)}
                data-testid={`email-pref-radio-${opt.value}`}
                className="mt-1 accent-[#1A422D]"
              />
              <div className="flex-1">
                <div className="text-[#1A1A1A] font-medium">{opt.label}</div>
                {opt.description && (
                  <div className="text-xs text-[#7A7A7A] mt-1 leading-relaxed">{opt.description}</div>
                )}
              </div>
            </label>
          );
        })}
      </fieldset>

      {feedback && (
        <div
          className={`mt-4 text-xs ${feedback.type === "error" ? "text-[#a33]" : "text-[#1A422D]"}`}
          data-testid="email-pref-feedback"
        >
          {feedback.text}
        </div>
      )}

      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          data-testid="email-pref-save"
          className="btn-primary"
        >
          {busy ? "Saving…" : "Save Preference"}
        </button>
      </div>
    </div>
  );
}
